import { apolloClient } from '../../shared/state';
import { GET_FEATURE_FLAGS } from './graphql';

export const LD_CACHE_KEY = 'ld_flags_cache';
export const LD_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in ms

// Flag keys read at the application-shell level. Kept here rather than inline so
// the shell resolves each one exactly once.
export const REVAMP_SHELL_FLAG = 'fit-5700-revamp-fitune-sidebar-navbar';

export function saveFlagCache(flags) {
  try {
    localStorage.setItem(LD_CACHE_KEY, JSON.stringify({ flags, cachedAt: Date.now() }));
  } catch {
    // Ignore storage write failures (quota, privacy mode); fallback remains in memory.
  }
}

function readFlagCache() {
  try {
    const raw = localStorage.getItem(LD_CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw);
    if (!cache || typeof cache.cachedAt !== 'number' || !cache.flags) return null;
    return cache;
  } catch {
    return null;
  }
}

export function loadFlagCache() {
  const cache = readFlagCache();
  if (!cache) return null;
  if (Date.now() - cache.cachedAt > LD_CACHE_TTL) return null;
  return cache;
}

export function loadStaleFlagCache() {
  return readFlagCache();
}

// The backend returns each flag as a raw value (LaunchDarkly all_flags_state),
// e.g. { 'flag-key': true }. Support the documented { value, variation } shape
// too so the fallback works regardless of the backend payload format.
export function readFlagValue(entry) {
  if (entry && typeof entry === 'object' && 'value' in entry) {
    return entry.value;
  }
  return entry;
}

// Synchronous best-effort read for flags that must be known before first paint,
// such as the ones that pick which application chrome renders. A stale cache
// entry is preferred over no value at all: an authoritative `ldClient.variation`
// read still runs afterwards and corrects it.
//
// Returns `undefined` when nothing is cached for the key, rather than a
// default: the caller is choosing what to mount, and a wrong guess is not a
// harmless default but a second chrome and a second application startup. Let it
// decide between guessing and waiting for `fetchFlagsFromBackend`.
export function readCachedFlagEntry(key) {
  const flags = (loadFlagCache() ?? loadStaleFlagCache())?.flags;
  if (!flags || !(key in flags)) return undefined;
  return readFlagValue(flags[key]);
}

export async function fetchFlagsFromBackend() {
  try {
    const { data } = await apolloClient.query({
      query: GET_FEATURE_FLAGS,
      fetchPolicy: 'no-cache'
    });
    const flags = data?.featureFlags;
    if (!flags) return null;
    saveFlagCache(flags);
    return flags;
  } catch {
    return null;
  }
}

export function createLdClientWithFallback(realClient) {
  let sdkReady = false;
  let fallbackFlags = loadFlagCache()?.flags ?? null;
  let warmInFlight = null;

  // Warm the cache only when it is missing or stale, and dedupe concurrent
  // calls so the many components that await initialization do not each trigger
  // a backend request. After a successful fetch the cache stays fresh for
  // LD_CACHE_TTL, so the next warm only hits the backend once it expires.
  const warmCache = () => {
    if (loadFlagCache() || warmInFlight) return;
    warmInFlight = fetchFlagsFromBackend().finally(() => {
      warmInFlight = null;
    });
  };

  const ensureFallback = async () => {
    fallbackFlags =
      loadFlagCache()?.flags ??
      (await fetchFlagsFromBackend()) ??
      loadStaleFlagCache()?.flags ??
      fallbackFlags;
  };

  // Use 'initialized' (success) rather than 'ready', which the SDK also fires on
  // failed startup. A 'change' event means live flag data arrived (initial load
  // or recovery), so it is also a valid signal to switch to live values.
  // sdkReady only ever flips false -> true: once the SDK has good in-memory
  // values, a transient streaming error must not drop us back to the fallback.
  realClient.on('initialized', () => {
    sdkReady = true;
    warmCache();
  });
  realClient.on('change', () => {
    sdkReady = true;
  });
  realClient.on('failed', () => {
    ensureFallback();
  });
  realClient.on('error', () => {
    ensureFallback();
  });

  return {
    waitForInitialization: async timeout => {
      try {
        await realClient.waitForInitialization(timeout);
        sdkReady = true;
        warmCache();
      } catch (error) {
        await ensureFallback();
        if (!fallbackFlags) throw error;
      }
    },
    variation: (key, defaultValue) => {
      if (sdkReady) return realClient.variation(key, defaultValue);
      if (fallbackFlags && key in fallbackFlags) {
        const value = readFlagValue(fallbackFlags[key]);
        return value === undefined ? defaultValue : value;
      }
      return defaultValue;
    },
    on: (...args) => realClient.on(...args),
    off: (...args) => realClient.off(...args),
    identify: (...args) => realClient.identify(...args),
    track: (...args) => realClient.track(...args),
    close: (...args) => realClient.close(...args)
  };
}
